import { useId, type MouseEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import type { ConversationThread } from '@/hooks/useConversationThreads';
import { MessageSquare, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatUiRelativeTime } from '@/lib/ui-format';

const STAGE_LABELS: Record<string, string> = {
  initial_reply: 'New Reply',
  discovery: 'Discovery',
  qualifying: 'Qualifying',
  objection_handling: 'Objection',
  next_step: 'Next Step',
  meeting_booked: 'Meeting',
  graceful_exit: 'Closing',
};

const STAGE_COLORS: Record<string, string> = {
  initial_reply: 'bg-primary/15 text-foreground',
  discovery: 'bg-secondary text-secondary-foreground',
  qualifying: 'bg-status-warning/10 text-status-warning',
  objection_handling: 'bg-destructive/15 text-foreground',
  next_step: 'bg-primary/15 text-foreground',
  meeting_booked: 'bg-status-success/10 text-status-success',
  graceful_exit: 'bg-muted text-muted-foreground',
};

/**
 * Exactly the fields this component reads — a `Pick`, not the full
 * `ConversationThread`, so a caller that only has (or wants to fetch) a
 * display-only projection isn't forced to over-fetch the rest. A full
 * `ConversationThread` still satisfies this structurally, so the main inbox
 * list (which does have the full shape) needs no changes.
 */
export type ThreadListItemThread = Pick<
  ConversationThread,
  | 'current_stage'
  | 'pending_draft_count'
  | 'lead_name'
  | 'last_message_at'
  | 'lead_title'
  | 'lead_company'
  | 'last_message_preview'
  | 'last_message_sender'
  | 'message_count'
  | 'sender_name'
> &
  Partial<Pick<ConversationThread, 'campaign_name' | 'follow_up_due_at'>>;

interface ThreadListItemProps {
  thread: ThreadListItemThread;
  isSelected: boolean;
  leadNameId?: string;
  /**
   * Stable ID owned by a queue-row wrapper. Resolved campaigns apply it to
   * the badge rendered here; unresolved campaigns may point it at the
   * sibling fallback badge while keeping the row action's description intact.
   */
  campaignDescriptionId?: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  /**
   * The default inbox list opens a conversation in place (a pressed/selected
   * toggle). Reply Queue rows navigate straight to the lead's dossier and
   * have no "selected" state — `aria-pressed` on them would announce a
   * toggle action that isn't real, so 'dossier' mode omits it and relabels
   * the accessible name to describe what the click actually does.
   */
  mode?: 'conversation' | 'dossier';
  /**
   * Follow-up tab only. Swaps the row's timestamp from "how old is the last message" to "how long
   * has this been due", because that is the number the queue is ordered by.
   */
  showFollowUpDue?: boolean;
}

function formatTimeAgo(dateStr: string): string {
  return formatUiRelativeTime(dateStr);
}

export function ThreadListItem({
  thread,
  isSelected,
  leadNameId,
  campaignDescriptionId,
  onClick,
  mode = 'conversation',
  showFollowUpDue = false,
}: ThreadListItemProps): JSX.Element {
  const fallbackLeadNameId = useId();
  const openConversationLabelId = useId();
  const pendingDescriptionId = useId();
  const fallbackCampaignDescriptionId = useId();
  const resolvedLeadNameId = leadNameId ?? fallbackLeadNameId;
  const resolvedCampaignDescriptionId =
    campaignDescriptionId ?? fallbackCampaignDescriptionId;
  const stageLabel = STAGE_LABELS[thread.current_stage] || thread.current_stage;
  const stageColor =
    STAGE_COLORS[thread.current_stage] || 'bg-muted text-muted-foreground';
  const hasPending = thread.pending_draft_count > 0;
  // Only when the tab asked for it AND a date exists — a row with no deferral must never claim one.
  const dueLabel =
    showFollowUpDue && thread.follow_up_due_at
      ? `Due ${formatTimeAgo(thread.follow_up_due_at)}`
      : null;
  const isDossierMode = mode === 'dossier';
  const descriptionIds = [
    thread.campaign_name || campaignDescriptionId
      ? resolvedCampaignDescriptionId
      : null,
    hasPending ? pendingDescriptionId : null,
  ].filter((id): id is string => id !== null);
  const initials = thread.lead_name
    .split(' ')
    .map((namePart) => namePart[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <button
      type="button"
      data-testid="thread-item"
      onClick={onClick}
      aria-pressed={isDossierMode ? undefined : isSelected}
      aria-labelledby={`${openConversationLabelId} ${resolvedLeadNameId}`}
      aria-describedby={descriptionIds.length > 0 ? descriptionIds.join(' ') : undefined}
      className="block min-h-11 w-full rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <span id={openConversationLabelId} className="sr-only">
        {isDossierMode ? 'Open lead dossier for' : 'Open conversation with'}
      </span>
      {hasPending && (
        <span id={pendingDescriptionId} className="sr-only">
          {thread.pending_draft_count}{' '}
          {thread.pending_draft_count === 1 ? 'pending draft' : 'pending drafts'}
        </span>
      )}
      <Card
        className={cn(
          'h-full transition-colors hover:bg-accent/50',
          isSelected && 'border-primary bg-accent',
          hasPending && 'border-l-4 border-l-status-warning'
        )}
      >
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-medium text-foreground"
              data-content-origin="external"
            >
              {initials}
            </span>

            <span className="min-w-0 flex-1">
              <span className="flex items-center justify-between gap-2">
                <span
                  id={resolvedLeadNameId}
                  className="truncate text-sm font-semibold"
                  data-content-origin="external"
                >
                  {thread.lead_name}
                </span>
                {/*
                  On the Follow-up tab the row is ordered by the DEADLINE, so showing the age of
                  the last message would explain the wrong thing: the date is deliberately 45 days
                  after that message, and "2 months ago" says nothing about how overdue this is.
                */}
                <span className="shrink-0 text-xs text-muted-foreground">
                  {dueLabel ?? (thread.last_message_at ? formatTimeAgo(thread.last_message_at) : '')}
                </span>
              </span>

              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {thread.lead_title && (
                  <span
                    className="truncate"
                    data-content-origin="external"
                  >
                    {thread.lead_title}
                  </span>
                )}
                {thread.lead_title && thread.lead_company && <span>@</span>}
                {thread.lead_company && (
                  <span
                    className="truncate"
                    data-content-origin="external"
                  >
                    {thread.lead_company}
                  </span>
                )}
              </span>

              {thread.last_message_preview && (
                <span
                  className="mt-1 block line-clamp-1 text-xs text-muted-foreground"
                >
                  {thread.last_message_sender === 'us' && <span>You: </span>}
                  {thread.last_message_sender === 'us' ? (
                    <span data-content-origin="generated">
                      {thread.last_message_preview}
                    </span>
                  ) : (
                    <span data-content-origin="external">
                      {thread.last_message_preview}
                    </span>
                  )}
                </span>
              )}

              <span className="mt-1.5 flex items-center gap-1.5">
                {thread.campaign_name && (
                  <Badge
                    id={resolvedCampaignDescriptionId}
                    variant="secondary"
                    className="h-4 max-w-full truncate px-1 text-[10px]"
                    data-content-origin="domain"
                  >
                    {thread.campaign_name}
                  </Badge>
                )}

                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
                    stageColor
                  )}
                  data-content-origin="domain"
                >
                  {stageLabel}
                </span>

                {thread.message_count > 1 && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                    <MessageSquare className="h-3 w-3" aria-hidden="true" />
                    {thread.message_count}
                  </span>
                )}

                {hasPending && (
                  <Badge
                    variant="outline"
                    className="h-4 border-status-warning/50 px-1 text-[10px] text-status-warning"
                  >
                    {thread.pending_draft_count} pending
                  </Badge>
                )}

                {thread.sender_name && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                    <User className="h-3 w-3" aria-hidden="true" />
                    <span data-content-origin="domain">
                      {thread.sender_name}
                    </span>
                  </span>
                )}
              </span>
            </span>
          </div>
        </CardContent>
      </Card>
    </button>
  );
}
